import React from "react";
import { useTranslation } from "react-i18next";
import OrderRes from "types/res/order/OrderRes";
import DateTimeUtils, { FORMAT_TYPE } from "utils/DateTimeUtils";

type Props = {
  orders: OrderRes[];
  message: string;
};

/**
 * 支払通知書の HTML 版 (html2canvas で画像化 → jsPDF で PDF 化)。
 * 画面には表示されず、キャプチャ用に off-screen でレンダリングされる。
 */
const SpecPdf = React.forwardRef<HTMLDivElement, Props>(
  ({ orders, message }, ref) => {
    const { t } = useTranslation("settlement");
    if (!orders.length) return null;

    const sum = (key: keyof OrderRes) =>
      orders.reduce((s, o) => s + ((o[key] as number) || 0), 0);

    const borderStyle: React.CSSProperties = {
      border: "1px solid #bfbfbf",
      borderBottom: "1px solid #000",
    };

    const cellStyle: React.CSSProperties = {
      padding: "6px 8px",
      fontSize: 10,
      border: "1px solid #bfbfbf",
      textAlign: "right",
      verticalAlign: "middle",
    };

    const cellTextStyle: React.CSSProperties = {
      ...cellStyle,
      textAlign: "left",
    };

    const headerCellStyle: React.CSSProperties = {
      ...cellStyle,
      fontSize: 11,
      fontWeight: "bold",
      textAlign: "center",
      backgroundColor: "#f5f5f5",
      ...borderStyle,
    };

    return (
      <div
        ref={ref}
        style={{
          width: 1800,
          padding: 24,
          backgroundColor: "#fff",
          fontFamily:
            "'Yu Gothic', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
          color: "#000",
          boxSizing: "border-box",
        }}
      >
        <h1
          style={{
            textAlign: "center",
            fontSize: 28,
            fontWeight: "bold",
            margin: "10px 0 20px",
          }}
        >
          {t("pdf.title")}
        </h1>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <thead>
            <tr>
              <th style={headerCellStyle}>{t("pdf.columns.date")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.guestName")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.shopName")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.castName")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.courseName")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.course")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.extension")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.designate")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.other")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.transport")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.discount")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.medium")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.cosplay")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.option")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.welfare")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.invoice")}</th>
              <th style={headerCellStyle}>{t("pdf.columns.total")}</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.orderId}>
                <td style={cellStyle}>
                  {DateTimeUtils.toFormatAsLocalTimezone(
                    order.orderDate,
                    FORMAT_TYPE.YEAR_DAY
                  )}
                </td>
                <td style={cellTextStyle}>{order?.guest?.name || ""}</td>
                <td style={cellTextStyle}>{order?.shop?.name || ""}</td>
                <td style={cellTextStyle}>
                  {order?.castName?.name}
                  {order?.castName?.cast?.name
                    ? ` (${order.castName.cast.name})`
                    : ""}
                </td>
                <td style={cellTextStyle}>{order?.course?.name || ""}</td>
                <td style={cellStyle}>{order?.courseCastFee || 0}{t("units.yen")}</td>
                <td style={cellStyle}>{order?.additionalCastFee || 0}{t("units.yen")}</td>
                <td style={cellStyle}>{order?.designateCastFee || 0}{t("units.yen")}</td>
                <td style={cellStyle}>{order?.otherCastFee || 0}{t("units.yen")}</td>
                <td style={cellStyle}>{order?.areaCastFee || 0}{t("units.yen")}</td>
                <td style={cellStyle}>{order?.discountCastFee || 0}{t("units.yen")}</td>
                <td style={cellStyle}>{order?.mediumCastFee || 0}{t("units.yen")}</td>
                <td style={cellStyle}>{order?.cosplayCastFee || 0}{t("units.yen")}</td>
                <td style={cellStyle}>{order?.optionCastFee || 0}{t("units.yen")}</td>
                <td style={cellStyle}>{order?.welfareFee || 0}{t("units.yen")}</td>
                <td style={cellStyle}>{order?.invoiceFee || 0}{t("units.yen")}</td>
                <td style={cellStyle}>{order?.totalCastFee || 0}{t("units.yen")}</td>
              </tr>
            ))}
            <tr>
              <td
                colSpan={5}
                style={{ ...cellStyle, textAlign: "right", fontWeight: "bold" }}
              >
                {t("pdf.totalRow")}
              </td>
              <td style={cellStyle}>{sum("courseCastFee")}{t("units.yen")}</td>
              <td style={cellStyle}>{sum("additionalCastFee")}{t("units.yen")}</td>
              <td style={cellStyle}>{sum("designateCastFee")}{t("units.yen")}</td>
              <td style={cellStyle}>{sum("otherCastFee")}{t("units.yen")}</td>
              <td style={cellStyle}>{sum("areaCastFee")}{t("units.yen")}</td>
              <td style={cellStyle}>{sum("discountCastFee")}{t("units.yen")}</td>
              <td style={cellStyle}>{sum("mediumCastFee")}{t("units.yen")}</td>
              <td style={cellStyle}>{sum("cosplayCastFee")}{t("units.yen")}</td>
              <td style={cellStyle}>{sum("optionCastFee")}{t("units.yen")}</td>
              <td style={cellStyle}>{sum("welfareFee")}{t("units.yen")}</td>
              <td style={cellStyle}>{sum("invoiceFee")}{t("units.yen")}</td>
              <td style={cellStyle}>{sum("totalCastFee")}{t("units.yen")}</td>
            </tr>
          </tbody>
        </table>
        <div
          style={{
            fontSize: 16,
            textAlign: "right",
            marginTop: 30,
            marginRight: 5,
          }}
        >
          {t("pdf.companyLabel")}{orders?.[0]?.company?.name || ""}
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: "bold",
            marginLeft: 5,
            marginTop: 20,
          }}
        >
          {t("pdf.messageTitle")}
        </div>
        <div
          style={{
            padding: 10,
            border: "1px solid #000",
            margin: 5,
            fontSize: 12,
            minHeight: 60,
            whiteSpace: "pre-wrap",
          }}
        >
          {message}
        </div>
      </div>
    );
  }
);

SpecPdf.displayName = "SpecPdf";
export default SpecPdf;
